import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const PageWrapper = styled(Box)(() => ({
  maxWidth: 480,
  margin: "0 auto",
  padding: "16px 16px",
}));

const Header = styled(Paper)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 1000,
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#F3F4F6", // light grey
  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
  borderRadius: 0,
}));

const InfoCard = styled(Paper)(() => ({
  position: "relative",
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  marginTop: 30,
}));

const NoticeCard = styled(Paper)(() => ({
  borderRadius: 12,
  padding: 16,
  backgroundColor: "#FFF7ED",
  marginBottom: 24,
}));

const Row = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
}));

const Label = styled(Typography)(() => ({
  fontSize: 12,
  color: "#6B7280",
}));

const Value = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 600,
  color: "#111827",
}));

const StickyButton = styled(Box)(() => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  padding: 16,
  backgroundColor: "#ffffff",
  borderTop: "1px solid #E5E7EB",
}));

const MedPayAccountDetails: React.FC = () => {
  const navigate = useNavigate();
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setToastMessage(`${label} copied`);
    setToastOpen(true);
  };

  return (
    <>
      <PageWrapper>
        {/* Header */}
        <Header elevation={1}>
          <Typography fontSize={16} fontWeight={600} color="#111827">
            MedPay Account Details
          </Typography>
        </Header>

        {/* Beneficiary Details */}
        <InfoCard elevation={1}>
          <Typography fontWeight={600} mb={2}>
            Transfer to this beneficiary
          </Typography>

          <Chip
            label="USE IMPS / NEFT / RTGS"
            size="small"
            sx={{
              position: "absolute",
              top: -10,
              right: 12,
              backgroundColor: "#E6F4EA",
              color: "#15803D",
              fontWeight: 600,
              fontSize: "11px",
              height: 24,
            }}
          />

          <Row>
            <Box>
              <Label>Account Number</Label>
              <Value>755005000448</Value>
            </Box>
            <IconButton
              onClick={() => copyText("755005000448", "Account number")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label>Account Type</Label>
              <Value>Current</Value>
            </Box>
            <IconButton onClick={() => copyText("Current", "Account type")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label>Bank Name</Label>
              <Value>ICICI BANK</Value>
            </Box>
            <IconButton onClick={() => copyText("ICICI BANK", "Bank name")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label>IFSC Code</Label>
              <Value>ICIC0007550</Value>
            </Box>
            <IconButton onClick={() => copyText("ICIC0007550", "IFSC code")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label>Branch</Label>
              <Value>INDIRANAGAR 100 FEET ROAD, BANGALORE</Value>
            </Box>
            <IconButton
              onClick={() =>
                copyText("INDIRANAGAR 100 FEET ROAD, BANGALORE", "IFSC code")
              }
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Divider />

          <Row mt={2}>
            <Box>
              <Label>Beneficiary Name</Label>
              <Value>MEDWAY TECHNOLOGIES PRIVATE LIMITED PRIME ACCOUNT</Value>
            </Box>
            <IconButton
              onClick={() =>
                copyText(
                  "MEDWAY TECHNOLOGIES PRIVATE LIMITED PRIME ACCOUNT",
                  "Beneficiary name",
                )
              }
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Divider sx={{ my: 2 }} />

          <Box
            display="flex"
            alignItems="center"
            justifyContent="left"
            gap={1}
            mt={1}
          >
            <WarningAmberIcon fontSize="small" sx={{ color: "#F59E0B" }} />
            <Typography fontSize={12} color="#92400E">
              Please specify the firm name under remarks.
            </Typography>
          </Box>
        </InfoCard>

        {/* Notice */}
        <Typography fontWeight={600} mb={1} mt={5}>
          Please note before transferring
        </Typography>

        <NoticeCard elevation={0}>
          <Typography fontSize={13} mb={1}>
            • Transfers from non-registered bank accounts are usually reversed
            instantly by your bank.
          </Typography>
          <Typography fontSize={13} mb={1}>
            • If not instant, the reversal may take up to 10 days.
          </Typography>
          <Typography fontSize={13}>
            • Please transfer only via IMPS, NEFT, or RTGS from your registered
            bank account. UPI transfers are not supported.
          </Typography>
        </NoticeCard>

        <Snackbar
          open={toastOpen}
          autoHideDuration={2000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setToastOpen(false)}
            sx={{
              borderRadius: 2,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "& .MuiAlert-icon": {
                marginRight: "6px",
              },
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                textAlign: "center",
              },
            }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </PageWrapper>
    </>
  );
};

export default MedPayAccountDetails;
